import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useModals } from "@/context/ModalContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Loader2, CornerDownLeft } from "lucide-react";
import { fmtPrice, fmtChange, trendColor } from "@/utils/format";

export default function GlobalLookup() {
  const { openStockDetail } = useModals();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const mod = e.metaKey || e.ctrlKey;
      if (mod && k === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (k === "/" && !mod) {
        const el = document.activeElement;
        const tag = el?.tagName;
        const editable = el?.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
        if (!editable) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/market/search", { params: { q } })
        .then((r) => id === reqId.current && setResults(r.data.results))
        .catch(() => id === reqId.current && setResults([]))
        .finally(() => id === reqId.current && setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  const pick = (s) => {
    setOpen(false);
    openStockDetail(s.symbol);
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const typed = q.trim().toUpperCase();
    if (!typed) return;
    const exact = results.find((r) => r.symbol === typed || r.symbol.split(".")[0] === typed);
    pick(exact || results[0] || { symbol: typed });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-lookup-btn"
        aria-label="Look up a stock or crypto"
        title={`Look up a stock or crypto (${isMac ? "⌘K" : "Ctrl K"})`}
        className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors h-9 px-2.5 lg:pl-3 lg:pr-2"
      >
        <Search className="w-4 h-4" />
        <span className="hidden lg:inline text-xs font-medium">Lookup</span>
        <kbd className="hidden lg:inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-num">{isMac ? "⌘" : "Ctrl"} K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="global-lookup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Look up a stock or crypto</DialogTitle>
            <DialogDescription>Search any US / TSX stock or crypto — no account needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="global-lookup-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                autoFocus
                placeholder="AAPL, BTC-USD, TD.TO…"
                className="pl-9 pr-9 font-num"
              />
              {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
            </div>
            <div className="max-h-80 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="global-lookup-results">
              {results.map((s) => (
                <button
                  key={s.symbol}
                  type="button"
                  data-testid={`global-lookup-option-${s.symbol}`}
                  onClick={() => pick(s)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-num font-semibold text-sm">{s.symbol}</span>
                      <Badge variant="secondary" className="text-[9px]">{s.exchange}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{s.name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-num text-sm">{fmtPrice(s.price)}</div>
                    <div className={`font-num text-xs ${trendColor(s.change_percent)}`}>{fmtChange(s.change, s.change_percent)}</div>
                  </div>
                </button>
              ))}
              {q.trim() && !loading && results.length === 0 && (
                <button
                  type="button"
                  data-testid="global-lookup-add-typed"
                  onClick={() => pick({ symbol: q.trim().toUpperCase() })}
                  className="w-full flex items-center justify-between px-3 py-3 text-left hover:bg-secondary transition-colors"
                >
                  <span className="text-sm">Look up <span className="font-num font-semibold">{q.trim().toUpperCase()}</span></span>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> Enter</span>
                </button>
              )}
              {!q.trim() && (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Start typing a ticker…</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
